import { useCallback, useEffect, useMemo, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { useSession } from "../auth/useSession";
import { ApiError, fetchJson } from "./api";
import { keysFor, personalParams, restoredParams, type View, type SavedView } from "./viewPreference";
import { dataContextGuard } from "./dataContext";
import { isDemoMode } from "./env";

/** URL is the active view; only explicit controls write the personal preference.
 * Server compare-and-set prevents silent cross-tab/device overwrites. Mutations
 * serialize per actor/view and outlive route navigation, but not auth context.
 */
export function usePersonalView(view: View, { restoreToUrl = true, persist = true }: {
  restoreToUrl?: boolean;
  /** false: neither restore nor remember – the page always opens on its default (#630). */
  persist?: boolean;
} = {}) {
  const { user } = useSession();
  const demo = isDemoMode();
  const client = useQueryClient();
  const [url, setUrl] = useSearchParams();
  const key = useMemo(() => ["personal-accounting-view", user?.customer_id, user?.user_id, view], [user?.customer_id, user?.user_id, view]);
  const endpoint = `/api/view-preferences/${view}`;
  const query = useQuery({ queryKey: key, enabled: persist, queryFn: () => demo ? Promise.resolve<SavedView>({ revision: 0, params: {} }) : fetchJson<SavedView>(endpoint), retry: false, staleTime: 0, refetchOnWindowFocus: false });
  const explicit = !persist || (view === "files" && url.has("file")) || (view === "price_report" && url.size > 0) || (view !== "article_history" && url.has("view")) || (view === "offers" && url.has("angebot")) || keysFor(view).some(k => url.has(k));
  const params = useMemo(() => !explicit && query.data && !query.isFetching ? restoredParams(view, query.data, url) : url, [explicit, query.data, query.isFetching, url, view]);
  useEffect(() => {
    if (restoreToUrl && !explicit && params.toString() !== url.toString()) setUrl(params, { replace: true });
  }, [explicit, params, url, setUrl, restoreToUrl]);
  const blocked = useRef(false);
  const target = useRef<Record<string, string> | null>(null);
  const mutation = useMutation({
    scope: { id: JSON.stringify(key) },
    mutationFn: async ({ values, guard }: { values: Record<string, string>; guard: () => void }) => {
      guard();
      if (blocked.current) throw new Error("Die persönliche Ansicht ist noch nicht gespeichert.");
      try {
        await client.cancelQueries({ queryKey: key, exact: true });
        guard();
        const saved = client.getQueryData<SavedView>(key) ?? (demo ? { revision: 0, params: {} } : await fetchJson<SavedView>(endpoint));
        guard();
        const next = demo ? { revision: saved.revision + 1, params: values } : await fetchJson<SavedView>(endpoint, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ expected_revision: saved.revision, params: values }) });
        guard(); client.setQueryData(key, next);
        return next;
      } catch (error) { blocked.current = true; throw error; }
    },
  });
  const { mutate } = mutation;
  const remember = useCallback((next: URLSearchParams) => {
    const values = personalParams(view, next);
    if (!persist || values === null) return; // Incomplete date drafts never replace the last valid preference.
    target.current = values;
    mutate({ values, guard: dataContextGuard() });
  }, [mutate, view]);
  const change = useCallback((next: URLSearchParams, options?: { replace?: boolean }) => {
    // An explicit empty selection must not restore the old saved filters.
    if (!keysFor(view).some(k => next.has(k))) next.set("view", "default");
    setUrl(next, options); remember(next);
  }, [setUrl, remember, view]);
  const reset = () => {
    const next = new URLSearchParams(url);
    for (const k of keysFor(view)) next.delete(k);
    if (view === "belege") next.delete("angebot");
    next.set("view", "default");
    if (view === "price_report") {
      for (const k of ["review", "invoice", "position", "position_offset", "case_article", "case_supplier", "visit_id", "new_tasks"]) next.delete(k);
      next.set("report", "0");
    }
    change(next);
  };
  const retry = async () => {
    const guard = dataContextGuard();
    const refreshed = await query.refetch();
    guard();
    if (refreshed.isError || !target.current) return;
    blocked.current = false;
    mutate({ values: target.current, guard });
  };
  return {
    params, change, setUrl, reset, retry,
    ready: explicit || (!query.isPending && !query.isFetching),
    pending: mutation.isPending,
    error: mutation.isError ? mutation.error instanceof ApiError && mutation.error.status === 409
      ? "Deine Ansicht wurde in einem anderen Fenster geändert. Die aktuelle Auswahl ist noch nicht gemerkt."
      : "Die aktuelle Auswahl konnte nicht gemerkt werden."
      : query.isError ? "Deine persönliche Ansicht konnte nicht geladen werden. Es gelten die sichtbaren Einstellungen." : null,
    retryLabel: mutation.isError ? "Diese Auswahl merken" : "Erneut laden",
    saved: mutation.isSuccess,
    savedParams: query.data?.params,
    /** The visible selection differs from the page default, so resetting changes something. */
    customized: keysFor(view).some(k => params.has(k)),
    demo,
  };
}
export type PersonalViewState = ReturnType<typeof usePersonalView>;
